/**
 * Privacy Policy Modal Component
 * Displays privacy policy information in a modal overlay
 */

import { i18n } from '../utils/i18n'

export class PrivacyModal {
  private modal: HTMLElement | null = null

  constructor() {
    this.createModal()
    this.attachEventListeners()
  }

  /**
   * Create the modal element
   */
  private createModal(): void {
    const modal = document.createElement('div')
    modal.id = 'privacy-modal'
    modal.className = 'privacy-modal'
    modal.style.display = 'none'

    modal.innerHTML = `
      <div class="privacy-modal-overlay"></div>
      <div class="privacy-modal-content">
        <button class="privacy-modal-close" aria-label="Cerrar">&times;</button>
        <h2 class="privacy-modal-title" data-i18n="privacy.title">Política de Privacidad</h2>
        <div class="privacy-modal-body">
          <div class="privacy-section">
            <h3 data-i18n="privacy.responsible.title">Responsable del Tratamiento</h3>
            <p data-i18n="privacy.responsible.content">
              APRIORI LEGAL SAS. con domicilio en Medellín, Colombia, es responsable del tratamiento de los datos personales. Según nuestras políticas de tratamiento de datos personales, los mecanismos a través de los cuales hacemos uso de éstos son seguros y confidenciales, pues contamos con los medios tecnológicos idóneos para asegurar que sean almacenados de manera tal que se impida el acceso indeseado por parte de terceras personas, y en ese mismo orden aseguramos la confidencialidad de los mismos.
            </p>
          </div>

          <div class="privacy-section">
            <h3 data-i18n="privacy.contact.title">Cómo contactarnos</h3>
            <p><strong data-i18n="privacy.contact.address">Dirección oficinas:</strong> Calle 4 Sur # 43A-195</p>
            <p><strong data-i18n="privacy.contact.email">Correo electrónico:</strong> administracion@apriorilegal.co</p>
            <p><strong data-i18n="privacy.contact.phone">Teléfono:</strong> 3124453206</p>
          </div>

          <div class="privacy-section">
            <h3 data-i18n="privacy.purpose.title">Finalidades del Tratamiento</h3>
            <p data-i18n="privacy.purpose.intro">Sus datos personales serán incluidos en una base de datos y serán utilizados para las siguientes finalidades:</p>
            <ul>
              <li data-i18n="privacy.purpose.item1">Lograr una eficiente comunicación relacionada con nuestros productos, servicios, ofertas, promociones, alianzas, estudios, concursos, contenidos, así como los de nuestras compañías vinculadas, y para facilitarle el acceso general a la información de éstos;</li>
              <li data-i18n="privacy.purpose.item2">Proveer nuestros servicios y productos;</li>
              <li data-i18n="privacy.purpose.item3">Informar sobre nuevos productos o servicios que estén relacionados con el o los contratado(s) o adquirido(s);</li>
              <li data-i18n="privacy.purpose.item4">Dar cumplimiento a obligaciones contraídas con nuestros clientes, proveedores, y empleados;</li>
              <li data-i18n="privacy.purpose.item5">Informar sobre cambios de nuestros productos o servicios;</li>
              <li data-i18n="privacy.purpose.item6">Evaluar la calidad del servicio, y</li>
              <li data-i18n="privacy.purpose.item7">Realizar estudios internos sobre hábitos de consumo.</li>
            </ul>
          </div>

          <div class="privacy-section">
            <h3 data-i18n="privacy.rights.title">Sus Derechos</h3>
            <p data-i18n="privacy.rights.content">
              Se les informa a los titulares de información que pueden consultar el Manual Interno de Políticas y procedimientos de APRIORI LEGAL SAS, que contiene nuestras políticas para el tratamiento de la información recogida, así como los procedimientos de consulta y reclamación que le permitirán hacer efectivos sus derechos al acceso, consulta, rectificación, actualización y supresión de los datos, haciendo clic en el siguiente enlace: 
              <a href="https://www.besafecolombia.co" target="_blank" rel="noopener noreferrer">www.besafecolombia.co</a>
            </p>
          </div>

          <div class="privacy-section privacy-doc-link">
            <a href="/politica-tratamiento-datos-personales.pdf" target="_blank" rel="noopener noreferrer" data-i18n="privacy.pdfLink">Descargar la Política de Tratamiento de Datos Personales (PDF)</a>
          </div>
        </div>
      </div>
    `

    document.body.appendChild(modal)
    this.modal = modal
  }

  /**
   * Attach event listeners
   */
  private attachEventListeners(): void {
    if (!this.modal) return

    // Close button
    const closeBtn = this.modal.querySelector('.privacy-modal-close')
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close())
    }

    // Overlay click
    const overlay = this.modal.querySelector('.privacy-modal-overlay')
    if (overlay) {
      overlay.addEventListener('click', () => this.close())
    }

    // ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal?.style.display === 'flex') {
        this.close()
      }
    })

    // Listen for language changes
    window.addEventListener('languageChanged', () => {
      this.updateContent()
    })
  }

  /**
   * Update modal content with current language
   */
  private updateContent(): void {
    if (!this.modal) return

    const elements = this.modal.querySelectorAll('[data-i18n]')
    elements.forEach((element) => {
      const key = element.getAttribute('data-i18n')
      if (key) {
        const translation = i18n.t(key)
        element.textContent = translation
      }
    })
  }

  /**
   * Open the modal
   */
  open(): void {
    if (this.modal) {
      this.modal.style.display = 'flex'
      document.body.style.overflow = 'hidden'
      this.updateContent()
    }
  }

  /**
   * Close the modal
   */
  close(): void {
    if (this.modal) {
      this.modal.style.display = 'none'
      document.body.style.overflow = 'auto'
    }
  }
}

// Create and export singleton instance
export const privacyModal = new PrivacyModal()
